import { FormattedMessage, useIntl } from "react-intl";
import { Disclosure } from "@/components/design/Disclosure";
import { Select } from "@/components/design/Select";
import { NumberStepper } from "@/components/design/NumberStepper";
import { PlusButton } from "@/components/design/PlusButton";
import { newCondition, type ConditionByTypename, type MatchCondition } from "../design-codec";
import type { MatchData } from "../match-design-types";
import { useRequirementsReferenceData } from "../use-requirements-reference-data";
import { RequirementControls } from "./row-primitives";

// Bewerbungs-Modus: benoetigte Berufserfahrung als Branche + Mindestjahre, gebunden an echte
// IndustryExperienceCondition (genesis). Die tatsaechlichen Jahre werden kandidatenseitig aus den
// Experience-Zeitraeumen berechnet; hier wird nur die Anforderung (Branche, Mindestjahre, Muss/Kann,
// Wichtigkeit) definiert. Index-basierte Keys sind stabil, weil Edits eine Condition in-place aendern.
type IndustryExperienceCondition = ConditionByTypename<"IndustryExperienceCondition">;

export function RecruitingExperience({
  data,
  update,
  readOnly = false,
  className = "",
}: {
  data: MatchData;
  update: (patch: Partial<MatchData>) => void;
  readOnly?: boolean;
  className?: string;
}) {
  const intl = useIntl();
  const { industries } = useRequirementsReferenceData();

  const rows = data.conditions.filter(
    (c): c is IndustryExperienceCondition => c.__typename === "IndustryExperienceCondition",
  );

  function writeRows(next: IndustryExperienceCondition[]) {
    const others = data.conditions.filter((c) => c.__typename !== "IndustryExperienceCondition");
    update({ conditions: [...others, ...next] as MatchCondition[] });
  }
  function patch(i: number, p: Partial<IndustryExperienceCondition>) {
    const cur = rows[i];
    if (!cur) return;
    const next = rows.slice();
    next[i] = { ...cur, ...p };
    writeRows(next);
  }
  function remove(i: number) {
    const next = rows.slice();
    next.splice(i, 1);
    writeRows(next);
  }
  function add() {
    const cond = newCondition("IndustryExperienceCondition", {
      industryId: "",
      minimumYears: 2,
      operator: "GE",
    });
    writeRows([...rows, cond]);
  }

  // Anzeige-Reihenfolge: Muss immer oben, Kann darunter (stabil); patch/remove via Original-Index.
  const displayRows = rows.map((r, i) => ({ r, i }));
  displayRows.sort((a, b) => Number(b.r.requirement === "MUST") - Number(a.r.requirement === "MUST"));

  if (rows.length === 0) {
    if (readOnly) return null;

    return (
      <div className="px-10 py-2" data-testid="recruiting-branche-add">
        <PlusButton
          label={intl.formatMessage({
            id: "match.recruiting.addBranche",
            defaultMessage: "Branche",
          })}
          onClick={add}
        />
      </div>
    );
  }

  return (
    <Disclosure
      className={`col-span-full ${className}`}
      title={intl.formatMessage({
        id: "match.recruiting.experienceTitle",
        defaultMessage: "Benötigte Berufserfahrung",
      })}
      defaultOpen
    >

      {/* mt-3 entfaellt: den Abstand zum Titel liefert jetzt der Disclosure-Body. */}
      <div className="space-y-2">
        {displayRows.map(({ r, i }) => (
          <div
            key={`branche-${i}`}
            data-testid="recruiting-branche-row"
            className="flex flex-nowrap items-center gap-3"
          >
            {/* Schloss vorne, konsistent zur Ergebnis-Darstellung (Status vorn). */}
            <RequirementControls
              requirement={r.requirement}
              onChange={(req) => patch(i, { requirement: req })}
              readOnly={readOnly}
            />
            <div className="min-w-0 flex-1">
              <Select
                value={r.industryId}
                onChange={(next) => patch(i, { industryId: next })}
                disabled={readOnly}
                placeholder={intl.formatMessage({
                  id: "match.recruiting.branchePlaceholder",
                  defaultMessage: "Branche wählen",
                })}
                options={industries}
              />
            </div>
            <span className="shrink-0 text-[11px] text-ink/45">
              <FormattedMessage id="match.recruiting.minYears" defaultMessage="mind." />
            </span>
            <NumberStepper
              value={r.minimumYears}
              onChange={(next) => patch(i, { minimumYears: next })}
              min={0.5}
              step={0.5}
              ariaLabel={intl.formatMessage({ id: "match.recruiting.years", defaultMessage: "Jahre" })}
            />
            <span className="shrink-0 text-[11px] text-ink/55">
              <FormattedMessage id="match.recruiting.yearsUnit" defaultMessage="Jahre" />
            </span>
            {!readOnly ? (
              <button
                type="button"
                onClick={() => remove(i)}
                data-testid="recruiting-branche-remove"
                aria-label={intl.formatMessage({ id: "common.remove", defaultMessage: "Entfernen" })}
                className="ml-auto shrink-0 rounded-md px-2 py-1 text-ink/35 hover:text-ink/70"
              >
                ×
              </button>
            ) : null}
          </div>
        ))}
        {!readOnly ? (
          <div className="pt-1" data-testid="recruiting-branche-add">
            <PlusButton
              label={intl.formatMessage({ id: "match.recruiting.addBranche", defaultMessage: "Branche" })}
              onClick={add}
            />
          </div>
        ) : null}
      </div>
    </Disclosure>
  );
}
